/* eslint-disable class-methods-use-this */
import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { EmulatedReceiptModalComponent } from '../../shared/emulated-receipt-modal/emulated-receipt-modal.component';
import { PrintCommands } from './receipt-generator.service';
import { AlignmentPosition, PrintCommandType } from './pinter.types';

interface PrinterOptions {
  port: string;
  emulation: string;
  paperWidth?: number;
}

@Injectable({
  providedIn: 'root',
})
export class EmulatedPrinterService {
  constructor(private modalCtrl: ModalController) {}

  public async print(commands: PrintCommands, options: PrinterOptions): Promise<any> {
    try {
      const paperWidthChars = options.paperWidth || 48;
      const receiptText = this.commandsToText(commands, paperWidthChars);
      const modal = await this.modalCtrl.create({
        component: EmulatedReceiptModalComponent,
        cssClass: 'emulated-receipt-modal',
        componentProps: {
          receiptText,
          paperWidthChars,
        },
      });
      await modal.present();
      await modal.onDidDismiss();
      return { message: 'Emulated receipt printed' };
    } catch (error) {
      throw new Error('Failed to render emulated receipt.');
    }
  }

  /**
   * Convert Star print commands into a fixed-width plain-text receipt.
   * Preserves space-padded columns used for quantities, totals, and tip lines.
   */
  private commandsToText(commands: PrintCommands, width: number): string {
    let alignment: AlignmentPosition = AlignmentPosition.LEFT;
    const lines: string[] = [];

    const pushText = (text: string) => {
      if (text == null || text === '') {
        return;
      }
      const rawLines = String(text).split('\n');
      rawLines.forEach((line, index) => {
        // Trailing newline on a command should create a blank line after content.
        if (index === rawLines.length - 1 && line === '' && rawLines.length > 1) {
          lines.push('');
          return;
        }
        lines.push(this.formatLine(line, alignment, width));
      });
    };

    commands.forEach(command => {
      if (PrintCommandType.APPEND_ALIGNMENT in command) {
        alignment = command[PrintCommandType.APPEND_ALIGNMENT] as AlignmentPosition;
        if (command.data) {
          pushText(command.data);
        }
        return;
      }

      if (PrintCommandType.APPEND in command) {
        pushText(command[PrintCommandType.APPEND]);
        return;
      }

      if (PrintCommandType.APPEND_EMPHASIS in command) {
        pushText(command[PrintCommandType.APPEND_EMPHASIS]);
        return;
      }

      if (PrintCommandType.APPEND_INVERT in command) {
        pushText(command[PrintCommandType.APPEND_INVERT]);
        return;
      }

      if (PrintCommandType.APPEND_LINE_FEED in command) {
        const count = Math.max(command[PrintCommandType.APPEND_LINE_FEED] ?? 1, 0);
        for (let i = 0; i < count; i++) {
          lines.push('');
        }
        return;
      }

      if (PrintCommandType.APPEND_QR_CODE in command) {
        const previousAlignment = alignment;
        alignment = AlignmentPosition.CENTER;
        pushText(`[QR: ${command[PrintCommandType.APPEND_QR_CODE]}]`);
        alignment = previousAlignment;
        return;
      }

      if (PrintCommandType.APPEND_CUT_PAPER in command) {
        lines.push('-'.repeat(width));
      }
    });

    return lines.join('\n').replace(/\n{4,}/g, '\n\n\n');
  }

  private formatLine(line: string, alignment: AlignmentPosition, width: number): string {
    // Already space-padded for fixed-width layout (totals, items, signature).
    if (/^\s+\S/.test(line) || /  +/.test(line) || /^x_+/.test(line)) {
      return line;
    }
    if (alignment === AlignmentPosition.CENTER && line.length < width) {
      const left = Math.floor((width - line.length) / 2);
      return `${' '.repeat(Math.max(0, left))}${line}`;
    }
    if (alignment === AlignmentPosition.RIGHT && line.length < width) {
      return `${' '.repeat(Math.max(0, width - line.length))}${line}`;
    }
    return line;
  }
}
